import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Search, MapPin, Award, Bed, Bath, Square, Star, Home as HomeIcon, TreePine, CornerDownLeft } from 'lucide-react';
import { HeroSkyline } from './HeroSkyline';
import { allProperties } from '../data/properties';
const cities = ['Lagos', 'Abuja', 'Ibadan', 'Enugu', 'Abeokuta'];
type Slide = {
  type: 'intro' | 'property';
  slug?: string;
  category?: 'house' | 'land';
  name?: string;
  location?: string;
  city?: string;
  price?: string;
  image: string;
  beds?: number;
  baths?: number;
  sqft?: string;
  plotSizes?: string[];
  tag?: string;
};
// Derive slides from real selling properties
const sellingProperties = allProperties.filter(p => p.status === 'Selling');
// Interleave houses and land so the rotation feels balanced
const sellingHousesList = sellingProperties.filter(p => p.category === 'house');
const sellingLandsList = sellingProperties.filter(p => p.category === 'land');
const interleaved: typeof sellingProperties = [];
const maxLen = Math.max(sellingHousesList.length, sellingLandsList.length);
for (let i = 0; i < maxLen; i++) {
  if (sellingHousesList[i]) interleaved.push(sellingHousesList[i]);
  if (sellingLandsList[i]) interleaved.push(sellingLandsList[i]);
}
const slides: Slide[] = [{
  type: 'intro',
  image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80'
}, ...interleaved.slice(0, 6).map((p, i): Slide => ({
  type: 'property',
  slug: p.slug,
  category: p.category,
  name: p.name,
  location: p.location.split(',').slice(0, 2).join(',').trim(),
  city: p.city,
  price: p.priceFrom,
  image: p.hero,
  beds: p.beds,
  baths: p.baths,
  sqft: p.sqft,
  plotSizes: p.plotSizes,
  tag: i === 0 ? 'Featured' : p.category === 'land' ? 'Land' : 'New'
}))];
function TypingPhrase({
  phrases
}: {
  phrases: string[];
}) {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  useEffect(() => {
    const current = phrases[phraseIndex];
    const typingSpeed = isDeleting ? 40 : 80;
    const pauseAtEnd = 1800;
    const timer = setTimeout(() => {
      if (!isDeleting) {
        if (displayed.length < current.length) {
          setDisplayed(current.slice(0, displayed.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), pauseAtEnd);
        }
      } else {
        if (displayed.length > 0) {
          setDisplayed(current.slice(0, displayed.length - 1));
        } else {
          setIsDeleting(false);
          setPhraseIndex(prev => (prev + 1) % phrases.length);
        }
      }
    }, typingSpeed);
    return () => clearTimeout(timer);
  }, [displayed, isDeleting, phraseIndex, phrases]);
  return <span className="inline-flex items-baseline" data-id="element-323">
      {displayed}
      <motion.span animate={{
      opacity: [1, 0, 1]
    }} transition={{
      duration: 0.9,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="inline-block w-[3px] h-[0.85em] bg-gold ml-1.5 not-italic" data-id="element-324" />
    </span>;
}
function MagneticButton({
  children,
  className,
  type = 'button'
}: {
  children: React.ReactNode;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const handleMouse = (e: React.MouseEvent<HTMLButtonElement>) => {
    const {
      clientX,
      clientY
    } = e;
    const {
      height,
      width,
      left,
      top
    } = ref.current!.getBoundingClientRect();
    setPosition({
      x: (clientX - (left + width / 2)) * 0.2,
      y: (clientY - (top + height / 2)) * 0.2
    });
  };
  return <motion.button ref={ref} type={type} onMouseMove={handleMouse} onMouseLeave={() => setPosition({
    x: 0,
    y: 0
  })} animate={{
    x: position.x,
    y: position.y
  }} transition={{
    type: 'spring',
    stiffness: 150,
    damping: 15,
    mass: 0.1
  }} className={className} data-id="element-325">
      {children}
    </motion.button>;
}
export function Hero() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeCity, setActiveCity] = useState('Lagos');
  const [searchQuery, setSearchQuery] = useState('');
  const [isPaused, setIsPaused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  // Typeahead suggestions: filter by query and active city
  const suggestions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const pool = allProperties;
    if (!q) {
      // No query: show top 6 results from the active city, prioritising Selling
      return [...pool].filter(p => p.city === activeCity).sort((a, b) => (a.status === 'Selling' ? -1 : 0) - (b.status === 'Selling' ? -1 : 0)).slice(0, 6);
    }
    return pool.filter(p => [p.name, p.location, p.city, p.type ?? ''].join(' ').toLowerCase().includes(q)).sort((a, b) => {
      // Prioritise name matches, then selling status
      const aName = a.name.toLowerCase().startsWith(q) ? 0 : 1;
      const bName = b.name.toLowerCase().startsWith(q) ? 0 : 1;
      if (aName !== bName) return aName - bName;
      return (a.status === 'Selling' ? -1 : 0) - (b.status === 'Selling' ? -1 : 0);
    }).slice(0, 7);
  }, [searchQuery, activeCity]);
  const goToProperty = (slug: string, category: 'house' | 'land') => {
    setShowSuggestions(false);
    navigate(`/${category === 'land' ? 'land' : 'houses'}/${slug}`);
  };
  useEffect(() => {
    setHighlightIdx(0);
  }, [searchQuery, activeCity]);
  const goNext = () => setActiveIndex(prev => (prev + 1) % slides.length);
  const goPrev = () => setActiveIndex(prev => (prev - 1 + slides.length) % slides.length);
  // Auto-cycle slides (pauses on user interaction)
  useEffect(() => {
    if (isPaused) return;
    const intervalMs = activeIndex === 0 ? 2500 : 4000;
    const timer = setTimeout(goNext, intervalMs);
    return () => clearTimeout(timer);
  }, [activeIndex, isPaused]);
  return <section ref={containerRef} className="relative min-h-screen w-full bg-bone overflow-hidden pt-28 pb-16" data-id="element-326">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" data-id="element-327">
        <div className="w-full h-full" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, #0F0F0F 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-328" />
      </div>

      {/* Animated SVG skyline underlay */}
      <HeroSkyline data-id="element-329" />

      <div className="relative max-w-[1280px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center" data-id="element-330">
        {/* Left: Content */}
        <div className="lg:col-span-6 flex flex-col" data-id="element-331">
          {/* Award badge */}
          <motion.div initial={{
          opacity: 0,
          y: -10,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} transition={{
          duration: 0.7,
          delay: 0.1
        }} className="inline-flex self-start items-center gap-2.5 bg-white border border-gold/30 rounded-full pl-1.5 pr-4 py-1.5 mb-8 shadow-sm" data-id="element-332">
            <div className="w-7 h-7 rounded-full bg-gold flex items-center justify-center" data-id="element-333">
              <Award className="w-4 h-4 text-white" strokeWidth={2} data-id="element-334" />
            </div>
            <div className="flex items-center gap-2" data-id="element-335">
              <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-gold" data-id="element-336">
                Award Winner 2025
              </span>
              <span className="w-1 h-1 rounded-full bg-ink/30" data-id="element-337" />
              <span className="text-[10px] font-medium text-ink/70" data-id="element-338">
                Best Real Estate Co.
              </span>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.15
        }} className="text-xs font-semibold tracking-[0.2em] uppercase text-ink/50 mb-5" data-id="element-339">
            Zylus Homes & Property — Nigeria
          </motion.div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-ink leading-[1.05] tracking-tight mb-6 min-h-[3em]" data-id="element-340">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="block" data-id="element-341">
              Discover the
            </motion.span>
            <span className="block italic text-gold" data-id="element-342">
              <TypingPhrase phrases={['ideal property', 'perfect home', 'dream estate', 'finest investment', 'right neighborhood']} data-id="element-343" />
            </span>
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.5
          }} className="block" data-id="element-344">
              here.
            </motion.span>
          </h1>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 1,
          delay: 0.7
        }} className="text-ink/60 text-base md:text-lg max-w-md leading-relaxed mb-8" data-id="element-345">
            We are committed to building strong, sustainable, proactive and
            long-term relationships with both corporate and private clients.
          </motion.p>

          {/* City chips */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.8
        }} className="mb-6" data-id="element-346">
            <div className="flex items-center gap-2 mb-3" data-id="element-347">
              <MapPin className="w-3.5 h-3.5 text-gold" data-id="element-348" />
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-ink/50" data-id="element-349">
                Browse by city
              </span>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-350">
              {cities.map(city => <button key={city} onClick={() => setActiveCity(city)} className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all ${activeCity === city ? 'bg-ink text-white shadow-md' : 'bg-white text-ink/70 border border-ink/10 hover:border-ink/30'}`} data-id="element-351">
                  {city}
                </button>)}
            </div>
          </motion.div>

          {/* Search bar */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.9
        }} className="relative max-w-xl mb-8" data-id="element-352">
            <form onSubmit={e => {
            e.preventDefault();
            if (suggestions.length > 0) {
              const sel = suggestions[highlightIdx] || suggestions[0];
              goToProperty(sel.slug, sel.category);
              return;
            }
            // No matches — fall back to category page filtered by city via URL
            navigate('/houses');
          }} className="bg-white rounded-2xl border border-ink/10 shadow-sm p-2 flex items-center gap-2" data-id="element-353">
              <div className="flex items-center gap-3 flex-1 px-3" data-id="element-354">
                <Search className="w-4 h-4 text-ink/40 shrink-0" data-id="element-355" />
                <input type="text" value={searchQuery} onChange={e => {
                setSearchQuery(e.target.value);
                setShowSuggestions(true);
              }} onFocus={() => setShowSuggestions(true)} onBlur={() => setTimeout(() => setShowSuggestions(false), 150)} onKeyDown={e => {
                if (!showSuggestions || suggestions.length === 0) return;
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setHighlightIdx(i => (i + 1) % suggestions.length);
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHighlightIdx(i => (i - 1 + suggestions.length) % suggestions.length);
                } else if (e.key === 'Escape') {
                  setShowSuggestions(false);
                }
              }} placeholder={`Search homes & land in ${activeCity}...`} className="bg-transparent flex-1 py-3 text-sm focus:outline-none placeholder:text-ink/40" aria-autocomplete="list" aria-expanded={showSuggestions} data-id="element-356" />
              </div>
              <MagneticButton type="submit" className="bg-gold hover:bg-gold-light text-white text-sm font-medium px-5 py-3 rounded-xl flex items-center gap-2 transition-colors" data-id="element-357">
                Search <ArrowRight className="w-4 h-4" data-id="element-358" />
              </MagneticButton>
            </form>

            {/* Typeahead dropdown */}
            <AnimatePresence data-id="element-359">
              {showSuggestions && suggestions.length > 0 && <motion.div initial={{
              opacity: 0,
              y: -8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.18
            }} className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl border border-ink/10 shadow-2xl shadow-ink/10 overflow-hidden z-30" role="listbox" data-id="element-360">
                  <div className="px-4 py-2 text-[10px] font-bold tracking-[0.2em] uppercase text-ink/40 border-b border-ink/5 flex items-center justify-between" data-id="element-361">
                    <span data-id="element-362">
                      {searchQuery.trim() ? `${suggestions.length} match${suggestions.length === 1 ? '' : 'es'}` : `Popular in ${activeCity}`}
                    </span>
                    <span className="hidden sm:flex items-center gap-1 normal-case tracking-normal text-ink/30" data-id="element-363">
                      <CornerDownLeft className="w-3 h-3" data-id="element-364" /> to open
                    </span>
                  </div>
                  <div className="max-h-[320px] overflow-y-auto" data-id="element-365">
                    {suggestions.map((s, i) => {
                  const isHouse = s.category === 'house';
                  const Icon = isHouse ? HomeIcon : TreePine;
                  const isHighlight = i === highlightIdx;
                  return <button key={s.id} type="button" onMouseDown={e => e.preventDefault()} onMouseEnter={() => setHighlightIdx(i)} onClick={() => goToProperty(s.slug, s.category)} role="option" aria-selected={isHighlight} className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${isHighlight ? 'bg-gold/10' : 'hover:bg-bone'}`} data-id="element-366">
                          <div className="w-10 h-10 rounded-lg bg-ink/5 overflow-hidden shrink-0" data-id="element-367">
                            <img src={s.hero} alt="" className="w-full h-full object-cover" data-id="element-368" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-369">
                            <div className="flex items-center gap-2" data-id="element-370">
                              <Icon className="w-3 h-3 text-gold shrink-0" data-id="element-371" />
                              <span className="text-sm font-semibold text-ink truncate" data-id="element-372">
                                {s.name}
                              </span>
                            </div>
                            <div className="text-[11px] text-ink/50 truncate mt-0.5" data-id="element-373">
                              {s.location}
                            </div>
                          </div>
                          <div className="shrink-0 flex flex-col items-end gap-1" data-id="element-374">
                            <span className={`text-[9px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full ${s.status === 'Selling' ? 'bg-emerald-500 text-white' : 'bg-ink/10 text-ink/60'}`} data-id="element-375">
                              {s.status}
                            </span>
                            <span className="text-[10px] text-ink/40" data-id="element-376">
                              {s.priceFrom}
                            </span>
                          </div>
                        </button>;
                })}
                  </div>
                </motion.div>}
              {showSuggestions && suggestions.length === 0 && searchQuery.trim() && <motion.div initial={{
              opacity: 0,
              y: -8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl border border-ink/10 shadow-xl px-4 py-6 text-center z-30" data-id="element-377">
                    <div className="text-sm text-ink/60 mb-2" data-id="element-378">
                      No estates match "
                      <span className="font-semibold text-ink" data-id="element-379">
                        {searchQuery}
                      </span>
                      "
                    </div>
                    <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => navigate('/houses')} className="text-xs font-bold tracking-widest uppercase text-gold hover:text-gold-dark" data-id="element-380">
                      Browse all homes →
                    </button>
                  </motion.div>}
            </AnimatePresence>
          </motion.div>

          {/* Secondary CTA */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1
        }} className="flex items-center gap-6 text-sm" data-id="element-381">
            <a href="#book" className="text-ink font-medium flex items-center gap-2 hover:text-gold transition-colors group" data-id="element-382">
              Book an inspection
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-383" />
            </a>
            <span className="w-px h-4 bg-ink/20" data-id="element-384" />
            <a href="#properties" className="text-ink/60 hover:text-ink transition-colors" data-id="element-385">
              Explore properties
            </a>
          </motion.div>
        </div>

        {/* Right: 3D Card Stack */}
        <div className="lg:col-span-6 relative h-[520px] md:h-[600px] flex items-center justify-center" data-id="element-386">
          <div className="relative w-full max-w-[440px] h-full" style={{
          perspective: '1500px'
        }} data-id="element-387">
            <AnimatePresence mode="popLayout" data-id="element-388">
              {slides.map((slide, i) => {
              const offset = (i - activeIndex + slides.length) % slides.length;
              const isActive = offset === 0;
              const isNext = offset === 1;
              const isAfterNext = offset === 2;
              if (!isActive && !isNext && !isAfterNext) return null;
              return <motion.div key={i} initial={{
                opacity: 0,
                scale: 0.7,
                y: 80,
                rotateY: -90
              }} animate={{
                opacity: isActive ? 1 : isNext ? 0.7 : 0.4,
                scale: isActive ? 1 : isNext ? 0.92 : 0.84,
                y: isActive ? 0 : isNext ? 30 : 60,
                rotateY: isActive ? 0 : isNext ? -8 : -14,
                rotateX: isActive ? 0 : 4,
                zIndex: 10 - offset
              }} exit={{
                opacity: 0,
                scale: 1.05,
                y: -100,
                rotateY: 90,
                transition: {
                  duration: 0.7,
                  ease: [0.7, 0, 0.84, 0]
                }
              }} transition={{
                duration: 0.9,
                ease: [0.16, 1, 0.3, 1]
              }} drag={isActive ? 'x' : false} dragElastic={0.4} dragConstraints={{
                left: 0,
                right: 0
              }} onDragStart={() => setIsPaused(true)} whileDrag={{
                scale: 1.03
              }} onDragEnd={(_, info) => {
                const swipe = info.offset.x;
                const velocity = info.velocity.x;
                if (swipe < -80 || velocity < -500) {
                  goNext();
                } else if (swipe > 80 || velocity > 500) {
                  goPrev();
                }
                setTimeout(() => setIsPaused(false), 4000);
              }} className={`absolute inset-0 ${isActive ? 'cursor-grab active:cursor-grabbing' : 'pointer-events-none'}`} style={{
                transformStyle: 'preserve-3d'
              }} data-id="element-389">
                    {slide.type === 'intro' ? <div className="relative w-full h-full rounded-3xl overflow-hidden shadow-2xl bg-ink" data-id="element-390">
                        <img src={slide.image} alt="Zylus Homes" className="absolute inset-0 w-full h-full object-cover opacity-40" data-id="element-391" />
                        <div className="absolute inset-0 bg-gradient-to-br from-ink/90 via-ink/70 to-ink/95" data-id="element-392" />

                        {/* Animated rings */}
                        <motion.div animate={{
                    rotate: 360
                  }} transition={{
                    duration: 30,
                    repeat: Infinity,
                    ease: 'linear'
                  }} className="absolute -top-20 -right-20 w-72 h-72 rounded-full border border-gold/20" data-id="element-393" />
                        <motion.div animate={{
                    rotate: -360
                  }} transition={{
                    duration: 40,
                    repeat: Infinity,
                    ease: 'linear'
                  }} className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full border border-gold/15" data-id="element-394" />

                        <div className="relative h-full flex flex-col items-center justify-center text-center p-10 text-white" data-id="element-395">
                          <motion.div initial={{
                      scale: 0,
                      rotate: -180
                    }} animate={{
                      scale: 1,
                      rotate: 0
                    }} transition={{
                      delay: 0.2,
                      type: 'spring',
                      stiffness: 100
                    }} className="w-20 h-20 rounded-full bg-gold flex items-center justify-center mb-6 shadow-2xl" data-id="element-396">
                            <span className="text-3xl font-serif text-white" data-id="element-397">
                              Z
                            </span>
                          </motion.div>
                          <motion.div initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      delay: 0.4
                    }} className="text-[10px] font-bold tracking-[0.3em] uppercase text-gold mb-3" data-id="element-398">
                            Welcome to
                          </motion.div>
                          <motion.h3 initial={{
                      opacity: 0,
                      y: 20
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      delay: 0.5
                    }} className="text-5xl font-serif mb-3" data-id="element-399">
                            Zylus Homes
                          </motion.h3>
                          <motion.p initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} transition={{
                      delay: 0.7
                    }} className="text-white/70 text-sm max-w-xs" data-id="element-400">
                            Your gateway to premium real estate across Nigeria
                          </motion.p>
                          <motion.div initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} transition={{
                      delay: 0.9
                    }} className="flex items-center gap-1 mt-6" data-id="element-401">
                            {Array.from({
                        length: 5
                      }).map((_, idx) => <Star key={idx} className="w-3 h-3 fill-gold text-gold" data-id="element-402" />)}
                          </motion.div>
                        </div>
                      </div> : <div className="relative w-full h-full rounded-3xl overflow-hidden shadow-2xl bg-white" data-id="element-403">
                        <img src={slide.image} alt={slide.name} className="absolute inset-0 w-full h-full object-cover" data-id="element-404" />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/30 to-transparent" data-id="element-405" />

                        {/* Top tag */}
                        {isActive && slide.tag && <motion.div initial={{
                    opacity: 0,
                    x: -20
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: 0.3
                  }} className="absolute top-5 left-5 bg-gold text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-full" data-id="element-406">
                            {slide.tag}
                          </motion.div>}

                        {/* City badge */}
                        {isActive && <motion.div initial={{
                    opacity: 0,
                    x: 20
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: 0.3
                  }} className="absolute top-5 right-5 bg-white/15 backdrop-blur-md border border-white/20 text-white text-[10px] font-semibold tracking-widest uppercase px-3 py-1.5 rounded-full flex items-center gap-1.5" data-id="element-407">
                            <MapPin className="w-3 h-3" data-id="element-408" />
                            {slide.city}
                          </motion.div>}

                        {/* Bottom content */}
                        <div className="absolute bottom-0 left-0 right-0 p-6 text-white" data-id="element-409">
                          <div className="text-xs font-medium text-gold mb-2 tracking-wide uppercase" data-id="element-410">
                            {slide.location}
                          </div>
                          <h3 className="text-2xl font-serif mb-3" data-id="element-411">
                            {slide.name}
                          </h3>

                          <div className="flex items-center gap-4 text-xs text-white/80 mb-4 pb-4 border-b border-white/10" data-id="element-412">
                            {slide.category === 'land' ? <span className="flex items-center gap-1.5" data-id="element-413">
                                <Square className="w-3.5 h-3.5" data-id="element-414" />
                                {slide.plotSizes?.join(' · ') || 'Plots'}
                              </span> : <>
                                {slide.beds && <span className="flex items-center gap-1.5" data-id="element-415">
                                    <Bed className="w-3.5 h-3.5" data-id="element-416" /> {slide.beds}{' '}
                                    Beds
                                  </span>}
                                {slide.baths && <span className="flex items-center gap-1.5" data-id="element-417">
                                    <Bath className="w-3.5 h-3.5" data-id="element-418" />{' '}
                                    {slide.baths} Baths
                                  </span>}
                                {slide.sqft && <span className="flex items-center gap-1.5" data-id="element-419">
                                    <Square className="w-3.5 h-3.5" data-id="element-420" />{' '}
                                    {slide.sqft}
                                  </span>}
                              </>}
                          </div>

                          <div className="flex items-center justify-between" data-id="element-421">
                            <span className="text-xl font-serif" data-id="element-422">
                              {slide.price}
                            </span>
                            <button type="button" onClick={() => {
                        if (slide.slug && slide.category) {
                          navigate(`/${slide.category === 'land' ? 'land' : 'houses'}/${slide.slug}`);
                        }
                      }} className="bg-white text-ink text-xs font-semibold px-4 py-2 rounded-full hover:bg-gold hover:text-white transition-colors flex items-center gap-1.5" data-id="element-423">
                              View <ArrowRight className="w-3 h-3" data-id="element-424" />
                            </button>
                          </div>
                        </div>
                      </div>}
                  </motion.div>;
            })}
            </AnimatePresence>
          </div>

          {/* Slide indicators */}
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5" data-id="element-425">
            {slides.map((_, i) => <button key={i} onClick={() => setActiveIndex(i)} aria-label={`Go to slide ${i + 1}`} className={`h-1 rounded-full transition-all ${activeIndex === i ? 'w-8 bg-gold' : 'w-1.5 bg-ink/20 hover:bg-ink/40'}`} data-id="element-426" />)}
          </div>

          {/* Swipe hint */}
          <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 text-[10px] font-mono uppercase tracking-widest text-ink/40 select-none" data-id="element-427">
            ← Swipe or drag →
          </div>
        </div>
      </div>
    </section>;
}